import { useMemo, useState } from 'react';

import { ChevronDownIcon } from '@braskai/icons';
import { Chip, useDeviceType } from '@braskai/ui-kit';

import { Stack, Typography } from '@mui/material';

import { prettifyPlanName } from '@/widgets/pricing/pricingCards/lib/utils/getPlanName';

import { useBalanceStore } from '@/entities/balance';
import { useCurrentUser } from '@/entities/user/currentUser';

import { Plan, PlanCategory } from '@/shared/types/Billing';

import { PricingCardFeatures } from '../PricingCardFeatures/PricingCardFeatures';
import { SContainer, STitleContainer } from './styled';

type CardHeaderProps = {
  isPriceActive: boolean;
  plan: Plan;
};

export const CardHeader = ({ isPriceActive, plan }: CardHeaderProps) => {
  const { data: user } = useCurrentUser();
  const { hasCanceledSubscription, hasExpiredSubscription, hasTrialingEnterprisePlan } = useBalanceStore();
  const { isMobile } = useDeviceType();
  const [isDescriptionOpened, setIsDescriptionOpened] = useState<boolean>(false);

  const handleToggleDescription = () => {
    setIsDescriptionOpened(!isDescriptionOpened);
  };

  const { name, description } = plan;

  const isEnterprise = plan.category === PlanCategory.Enterprise;

  const planLabel = useMemo(() => {
    const showCurrentLabel =
      user?.subscription &&
      !hasCanceledSubscription &&
      !hasExpiredSubscription &&
      !hasTrialingEnterprisePlan &&
      isPriceActive;

    if (showCurrentLabel) {
      return <Chip label="Current" color="success" />;
    }

    return null;
  }, [hasCanceledSubscription, hasExpiredSubscription, hasTrialingEnterprisePlan, isPriceActive, user?.subscription]);

  return (
    <Stack>
      <SContainer>
        <STitleContainer>
          <Typography color={isEnterprise ? 'common.white' : 'grey.900'} variant="title-3-sb">
            {prettifyPlanName(name)}
          </Typography>
          {planLabel}
        </STitleContainer>
        <Stack
          sx={{
            alignItems: 'center',
            flexDirection: 'row',
            gap: 4,
            marginBottom: isDescriptionOpened ? 8 : 0,
          }}
        >
          <Typography color={isEnterprise ? 'common.white' : 'grey.900'} variant="body-2-md">
            {description}
          </Typography>
          {isMobile && (
            <Stack
              sx={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                transform: isDescriptionOpened ? 'rotate(180deg)' : 'rotate(0deg)',
                transformOrigin: 'center',
              }}
            >
              <ChevronDownIcon
                color={isEnterprise ? 'white' : 'grey.900'}
                data-testid="card-header-toggle-description"
                height={20}
                width={20}
                onClick={handleToggleDescription}
              />
            </Stack>
          )}
        </Stack>
      </SContainer>
      {isDescriptionOpened && isMobile && (
        <PricingCardFeatures isEnterprise={isEnterprise} pricingCardActivePlan={plan} />
      )}
    </Stack>
  );
};
